import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleConnectReturn } from '../services/stripeConnectService';
import { Box, CircularProgress, Typography, Container } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * Stripe Connect Return Page
 * This page handles the redirect after Stripe Connect onboarding
 */
const StripeConnectReturn = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your account setup...');

  useEffect(() => {
    const processReturn = async () => {
      try {
        console.log('Processing Stripe Connect return...');

        // Call backend to check account status
        const response = await handleConnectReturn();

        if (response.success) {
          const { onboarded, detailsSubmitted, payoutsEnabled } = response.data;

          setStatus('success');

          if (onboarded && payoutsEnabled) {
            setMessage('Your bank account has been successfully connected! You can now receive payouts.');
          } else if (detailsSubmitted) {
            setMessage('Your account details have been submitted. Stripe is verifying your information.');
          } else {
            setMessage('Account setup in progress. You may need to complete additional steps.');
          }

          // Redirect to settings page after 3 seconds
          setTimeout(() => {
            navigate('/account-settings?tab=bank', { replace: true });
          }, 3000);
        } else {
          throw new Error('Failed to process account status');
        }
      } catch (error) {
        console.error('Error processing Stripe Connect return:', error);
        setStatus('error');
        setMessage('There was an issue processing your account setup. Please try again.');

        // Redirect to settings page after 5 seconds even on error
        setTimeout(() => {
          navigate('/account-settings?tab=bank', { replace: true });
        }, 5000);
      }
    };

    processReturn();
  }, [navigate]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          py: 4
        }}
      >
        {status === 'processing' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Processing...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon
              sx={{ fontSize: 80, color: 'success.main', mb: 3 }}
            />
            <Typography variant="h5" gutterBottom color="success.main">
              Success!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting you back to settings...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon
              sx={{ fontSize: 80, color: 'error.main', mb: 3 }}
            />
            <Typography variant="h5" gutterBottom color="error.main">
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting you back to settings...
            </Typography>
          </>
        )}
      </Box>
    </Container>
  );
};

export default StripeConnectReturn;
